import path from 'lodash/fp/path';
import pipe from 'lodash/fp/pipe';
import assocPath from 'lodash/fp/assocPath';
import isEmpty from 'lodash/fp/isEmpty';
import { cloneDeep } from 'lodash';
import uuid from 'uuid/v1';

import { pushToast } from 'containers/ToastManager/actions';
import { dispatch } from 'pluginServices/store';
import { STAGE_ELEMENT_TYPES } from 'pluginServices/editorState/constants';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { RTEConstants } from 'components/RichTextEditor/constants';
import { elementValue, pluginConfigs } from 'services/ActivityService/keyPaths';

import { IMAGE_URN_REGEX, NOT_COURSEWARE_ITEM, SERIALIZE } from './constants';
import { PLUGIN_SCOPE } from 'constants/selectionScopes';
import { STAGE_SELECTION_SCOPE } from '@pearsontechnology/aero-stage-presentation-framework/dist/rce/constants';

import {
  getTargetIndex,
  getTargetPath,
  getType,
} from './getters';

import messages from '../messages';

import {
  hasNoId,
} from './predicates';

import {
  IDENTITY,
  append,
  converge,
  fromProp,
  filterProps,
  incrementTargetIndex,
  insertAtIndex,
  merge,
  not,
  findAndReplace,
} from './utils';
import AssetManager from 'AssetManager/index';
import { INTERACTIVE } from 'services/ActivityService/elementTypes';

const NODE_OBJECT_BLOCK = 'block';

/**
 * @func computeCurrentPath
 * @desc given current editorState, current interactiveConfig return a function that computes the valid stage path of a stage element
 * @param {Objet} editorState - current selected stage element
 * @param {Object} stage - current stage
 * @return {Number[]} currentPath - defacto currently selected path
  * */
export const computeCurrentPath = ({ selectedPath }, { stage } = { stage: {} }) => {
  if (selectedPath) return selectedPath;
  return isEmpty(stage)
    ? [0, 0, 0, 0]
    : [path(['sections', 'length'], stage), 0, 0, 0];
};

/**
 * @func computeEditorSelection
 * @desc compute the appropriate selection for editorState after duplication onPaste
 * @param {Object} config
 * @param {String} config.screenId
 * @param {Number[]} config.selectedPath
 * @param {String} config.elementType
 * @param {String} [config.componentId]
 * @return {Object}
 * */
export const computeEditorSelection = ({ screenId, selectedPath, elementType, componentId, stageRef }) => ({
  path: [PLUGIN_SCOPE, screenId, ...STAGE_SELECTION_SCOPE],
  data: {
    selectedPath: incrementTargetIndex(selectedPath),
    type: elementType,
    ...(componentId && { componentId }),
    stageRef,
  },
});

async function _copy(value) {
  return window.navigator.clipboard.writeText(value);
}

export async function setClip(data, title) { // eslint-disable-line consistent-return
  try {
    const message = intl.formatMessage(messages.screenSuccess, { title });
    const type = getType(data);
    if (type !== 'INTERACTIVE' || hasNoId(data)) {
      throw NOT_COURSEWARE_ITEM;
    }

    const clip = JSON.stringify(data);
    await _copy(clip);

    dispatch(pushToast({ message }));
  } catch (error) {
    if (error.message !== NOT_COURSEWARE_ITEM) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    return data;
  }
}

export function getClip(event) {
  const clip = (event.clipboardData || window.clipboardData).getData('text');

  try {
    JSON.parse(clip);
    return clip;
  } catch (error) {
    return;
  }
}

/**
 * @func extractUrnFromString
 * @desc returns an array of urns from source string
 * @param {String} source
 * @returns {Array<String>}
 *
 * (source: string) => string[]
 */
export function extractUrnFromString(source) {
  if (typeof source !== 'string') throw new Error('Please check parameter type, must be string');

  const matchedArray = source.match(IMAGE_URN_REGEX);
  return matchedArray || [];
}

/**
 * @func getNewStageElements
 * @desc given a stagePath and a stageElement return a function that computes an updated stageElements list with the element inserted at the targt location
 * @param {Number[]} stagePath - current selected editor path
 * @param {Object} stageElement - spf element to be pasted
 * @param {Object} stageConfig - current interactive config
 * @return {Object[]} updated list of `stageElement`s for currently selected column
 *
 * (stagePath: Number[]) => (stageElement: Object) => (c: Object) => Object[]
  * */
export const getNewStageElements = (stagePath, stageElement) => {
  const targetPath = getTargetPath(stagePath);
  const targetIndex = getTargetIndex(stagePath);

  const _flow = pipe(
    path(targetPath),
    insertAtIndex(targetIndex)(stageElement),
  );

  return _flow;
};

/**
 * @func setNewStageElements
 * @desc given a stagePath and a stageElement return a function that takes a stageModel and computes and updated stageModel containing the updated stageElements list
 * @param {Number[]} stagePath - current selected editor path
 * @param {Object} stageElement - spf element to be pasted
 * @param {Object} stageConfig - current interactive config
 * @return {Object} new config for current interative
 *
 * (stagePath: Number[]) => (stageElement: Object) => (stageConfig: Object) => Object
  * */
export const setNewStageElements = (stagePath, stageElement) =>
  converge(
    assocPath(getTargetPath(stagePath)),
    [getNewStageElements(stagePath, stageElement), IDENTITY]
  );

/**
 * @func createComponentReference
 * @desc given a component's instance id create a pair that holds the instance id and a uniqe ref value
 * @param {String} componentId
 * @return {Object} componentReference
  * */
export const createComponentReference = (itemId) => ({ itemId, localRef: itemId ? uuid() : undefined });

/**
 * @func setNewComponents
 * @desc given a component ref return a function that takes an ineractive config and computes a new config with an updated component list
 * @param {String} componentId
 * @param {Object} stageConfig
 * @return {Object}
 *
 * (componentId: string) => (stageConfig: Object) => Object
  * */
export const setNewComponents = (componentRef) => converge(
  assocPath(['components']),
  [
    pipe(
      path(['components']),
      append(componentRef),
    ),
    IDENTITY,
  ]
);

const fromNewComponents = (compRef) => pipe(
  setNewComponents(compRef),
  fromProp('components'),
);

const fromNewStageElements = (p, e) => pipe(
  setNewStageElements(p, e),
  fromProp('stage'),
);

const isNotPrivateProp = (key) => not(/^_[a-zA-Z]/.test(key));
export const sanitizeProps = filterProps(isNotPrivateProp);

/**
 * @func registerNewElement
 * @desc generate a unique ref value for a given element
 * @desc if the element is a component plugin, assign the generated ref to its `localRef` property
 *
 * @param {Object} element - stage element being duplicated
 * @param {String} element.type - type of stage element
 *
 * @return {Object} element
  * */
export const registerNewElement = (element) => {
  const elementWithSerializedRefs = serializeOrUpdateRefsInPluginConfigs(element, SERIALIZE);
  const stringifiedElement = JSON.stringify(elementWithSerializedRefs);
  const regexForRef = /"ref":(["'])(.*?)\1/g;
  // use a function instead of string allows us to generate random uuids
  const replacementFunction = (_, p1) => `"ref": ${p1}${uuid()}${p1}`;
  const elementReplacedWithNewRefs = JSON.parse(stringifiedElement.replace(regexForRef, replacementFunction));
  return ({
    ...elementReplacedWithNewRefs,
    ...(element.type === STAGE_ELEMENT_TYPES.PLUGIN && { localRef: elementReplacedWithNewRefs.ref || uuid() }),
  });
};

export const generateNewDataIds = (element) => {
  if (element.type === STAGE_ELEMENT_TYPES.TEXT) {
    const clonedElement = cloneDeep(element);
    clonedElement?.value?.document?.nodes &&
      processBlockLevelNodesRecursively(clonedElement.value.document.nodes);
    return clonedElement;
  }
  return element;
};

export const processBlockLevelNodesRecursively = (nodes) => {
  nodes.forEach((node) => {
    if (node.object === NODE_OBJECT_BLOCK && node.data?.dataId) {
      node.data.dataId = uuid();
    }
    node.nodes && processBlockLevelNodesRecursively(node.nodes);
  });
};

/**
 * @func registerRowsAndColumns
 * @desc ensure that any newly created rows or columns within the stage model have a unique ref
 *
 * @param {Object} config
 * @param {Object} config.stage - Stage Model
 * @param {Array<{ rows: Array<{ columns: Array<{ stageElements: Array<Object> }> }> }>} config.stage.sections
 *
 * @return {Object} config
  * */
function registerRowsAndColumns(config) {
  // @TODO: Temporary Implementation Should be Replaced
  //
  // This function avoid mutation by returning a copy
  // but still makes assumptions about the structure and
  // the values of its input. It is also not testable
  // without having to mock environments and mock state.

  const _config = { ...config };
  const addRef = (data) => Object.assign(data, { ref: uuid() });

  _config.stage?.sections?.forEach((section) => {
    if (section && !section.ref) addRef(section);

    section?.rows?.forEach((row) => {
      if (row && !row.ref) addRef(row);

      row?.columns?.forEach((column) => {
        if (column && !column.ref) addRef(column);

        column?.stageElements?.forEach((element) => {
          if (!element.ref) addRef(element);
        });
      });
    });
  });

  return _config;
}

/**
 * Generate a mapping function for inserting elements into a given stage
 *
 * @func getNewStage
 * @param {Object} options
 * @param {Number[]} options.stagePath - target stage location path
 * @param {Object} options.stageElement - copied stage element data
 * @param {String} [options.componentId] - newly created componentId for copied stage components
 *
 * @return { (Object) => Object }
  * */
export const getNewStage = ({ stagePath, stageElement, _componentId }) => {
  const isComponent = stageElement.type === STAGE_ELEMENT_TYPES.PLUGIN && !!_componentId;
  const componentRef = createComponentReference(_componentId);

  return converge(merge, [
    IDENTITY,
    isComponent ? fromNewComponents(componentRef) : IDENTITY,
    fromNewStageElements(stagePath, {
      ...stageElement,
      ...(isComponent && { localRef: componentRef.localRef, ref: componentRef.localRef }),
    }),
  ]);
};

/**
 * @func reduceInteractiveConfig
 * @desc like a state reducer, but for interactive config objects
 *
 * @param {Object} accumulatedConfig
 * @param {Object} currentElement
 * @param {Number[]} _path - computed stagePath
 * @param {String} _componentId - interactive instance id for element if it is a component plugin
 *
 * @return {Object} accumulatedConfig
  * */
export function reduceInteractiveConfig(accumulatedConfig, currentElement) {
  
  const { _path: stagePath, _componentId, ..._stageElement } = currentElement;
  const stageElement = sanitizeProps(_stageElement);

  const computeConfig = pipe(
    getNewStage({ stagePath, stageElement, _componentId }),
    registerRowsAndColumns,
  );

  return computeConfig(accumulatedConfig);
}

/**
 * @func serializeOrUpdateRefsInPluginConfigs
 * @desc serializes or updates the refs in 
 * pluginConfigs of Text Element as per Inline Icon Node Type
 *
 * @param {Object} element - the element which is copied and to be pasted
 *
 * @return {Object} element - the element after updating refs 
 * in pluginConfigs of Text Element as per Inline Icon Node Type
 * 
 * */
export const serializeOrUpdateRefsInPluginConfigs =  (element, action) => {
  if (element.type !== STAGE_ELEMENT_TYPES.TEXT || isEmpty(element.config?.pluginConfigs)) 
    return element;

  const pluginConfigWithOldRefs = [];
  const docNodeRefs = [];
  const newPluginConfigs = {};
  
  const getDocNodeRefs = findAndReplace(elementValue, (parentObj) => {
    const documentNodes = parentObj?.value?.document?.nodes;
    // Make sure update stage element has documents nodes
    if(documentNodes) {
      documentNodes.forEach((docNode) => {
        const nodes = docNode?.nodes;
        for(const node of nodes) {
          if (node.type === RTEConstants.NODE_TYPE_INLINE_ICON || node.type === 'mathml-expression'){
            docNodeRefs.push(node.data.ref);
          }
        }
      });
    }
  });

  const getPluginConfigOfElement = findAndReplace(pluginConfigs, (parentObj) => {
    for (const [ref, value] of Object.entries(parentObj.pluginConfigs)) {
      pluginConfigWithOldRefs.push({
        ref,
        value,
      });
    }
  });

  const serializePluginConfigs = () => {
    docNodeRefs.forEach((ref) => {
      const index = pluginConfigWithOldRefs.findIndex((item) => item.ref === ref);
      if (ref === pluginConfigWithOldRefs[index].ref){
        newPluginConfigs[ref] = pluginConfigWithOldRefs[index].value;
      }
    }
    );
  };

  const updatePluginConfigs = () => {
    docNodeRefs.forEach((ref, index) => {
      newPluginConfigs[ref] = pluginConfigWithOldRefs[index].value;
    }
    );
  };

  getPluginConfigOfElement(element);
  getDocNodeRefs(element);

  if (action === SERIALIZE){
    serializePluginConfigs();
  } else{
    updatePluginConfigs();
  }
  
  element.config.pluginConfigs = newPluginConfigs;
  return element;
};


/**
 * @func identifyAndRegisterImagesOfElement
 * @desc identify image urns based on regex and register them with screen
 *
 * @param {Object} element
 * @param {string} screenId
 *
 * @return {Object} element
  * */
export const identifyAndRegisterImagesOfElement = (element, screenId) => {
  try {
    if(element.type !== STAGE_ELEMENT_TYPES.PLUGIN) {
      const imageUrns = JSON.stringify(element).match(IMAGE_URN_REGEX);
      if(imageUrns) {
        for(const urn of imageUrns) {
          AssetManager.informAssetUsage(screenId, INTERACTIVE, urn);
        }
      }
    }
  } catch (error) {
    // eslint-disable-next-line
    console.error(`error: failed to copy images from element - ${error}`);
  }

  return element;
};
